import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import api from '../api';
import { useAuth } from './AuthContext';

const RealtimeContext = createContext(null);

const EVENT_LABELS = {
  'order-created': 'New Sales Order',
  'dispatch-created': 'Dispatch Scheduled',
  'dispatch-confirmed': 'Dispatch Confirmed',
  'dispatch-updated': 'Dispatch Updated',
  'qc-hold': 'QC Hold Alert',
  'qc-verdict': 'QC Verdict Updated',
  'wo-status-changed': 'Work Order Updated',
  'invoice-created': 'Invoice Created',
  'po-approved': 'Purchase Order Approved',
};

const EVENT_SEVERITY = {
  'qc-hold': 'critical',
  'dispatch-confirmed': 'success',
  'dispatch-updated': 'success',
  'order-created': 'info',
  'invoice-created': 'info',
  'po-approved': 'success',
  'dispatch-created': 'info',
  'wo-status-changed': 'info',
  'qc-verdict': 'info',
};

function formatEventMessage(event, data) {
  switch (event) {
    case 'order-created':
      return `${data.order_no} — ${data.customer} × ${data.qty} units`;
    case 'dispatch-created':
      return `${data.dispatch_no} — ${data.customer}`;
    case 'dispatch-confirmed':
      return `Dispatched by ${data.by}`;
    case 'dispatch-updated':
      return `Dispatch ${data.dispatch_no} invoiced — ${data.invoice_no} (₹${data.invoice_amount?.toLocaleString('en-IN') ?? ''})`;
    case 'qc-hold':
      return `${data.product} · Batch ${data.batch} failed ${data.fails} param(s)`;
    case 'qc-verdict':
      return `Verdict: ${data.verdict?.toUpperCase()} by ${data.by}`;
    case 'wo-status-changed':
      return `Status → ${data.status?.replace('_', ' ')} by ${data.by}`;
    case 'invoice-created':
      return `${data.invoice_no} — ${data.customer} ₹${data.amount?.toLocaleString('en-IN')}`;
    case 'po-approved':
      return `Approved by ${data.by}`;
    default:
      return JSON.stringify(data);
  }
}

export function RealtimeProvider({ children }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);
  const channelsRef = useRef([]);

  const addNotification = useCallback((event, data, channel) => {
    const notif = {
      id: `${Date.now()}-${Math.random()}`,
      event,
      label: EVENT_LABELS[event] || event,
      message: formatEventMessage(event, data),
      severity: EVENT_SEVERITY[event] || 'info',
      channel,
      data,
      ts: new Date(),
      read: false,
    };
    setNotifications(prev => [notif, ...prev].slice(0, 50));
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const clearAll = useCallback(() => setNotifications([]), []);

  useEffect(() => {
    if (!user) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setConnected(false);
      return;
    }

    api.get('/realtime/config').then(({ data: cfg }) => {
      if (!cfg.socket_url || !cfg.api_key) return;

      const socket = io(cfg.socket_url, {
        transports: ['websocket'],
        auth: { apiKey: cfg.api_key },
        reconnection: true,
        reconnectionDelay: 2000,
        reconnectionDelayMax: 10000,
        reconnectionAttempts: Infinity,
      });

      socketRef.current = socket;

      socket.on('connect', () => {
        setConnected(true);
        const channels = [cfg.channels.global, cfg.channels.role];
        channelsRef.current = channels;
        channels.forEach(ch => socket.emit('subscribe', { channel: ch }));
      });

      socket.on('disconnect', () => setConnected(false));
      socket.on('connect_error', (err) => {
        console.warn('[Realtime] connect error:', err.message);
        setConnected(false);
      });

      // Listen on both channels for all defined events
      const allEvents = Object.keys(EVENT_LABELS);
      const listenChannels = [cfg.channels.global, cfg.channels.role];

      listenChannels.forEach(channel => {
        allEvents.forEach(evtName => {
          socket.on(`${channel}:${evtName}`, (payload) => {
            addNotification(evtName, payload, channel);
          });
        });
      });
    }).catch(err => {
      console.warn('[Realtime] config fetch failed:', err.message);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setConnected(false);
    };
  }, [user, addNotification]);

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <RealtimeContext.Provider value={{ notifications, connected, unreadCount, markAllRead, clearAll }}>
      {children}
    </RealtimeContext.Provider>
  );
}

export const useRealtime = () => useContext(RealtimeContext);
